import { useEffect, type CSSProperties, type ReactNode } from "react";
import QRCode from "react-qr-code";
import { resolveLocalized } from "@shared/i18n";
import { useScheme, slotActive } from "../schemeContext";
import type {
  ResolvedAnnouncementSlot,
  ResolvedQrSlot,
} from "../schemeContext";
import type { SlotSize } from "@shared/types/foyer";
import { useNow } from "../useNow";
import { startDwell } from "../../../lib/engagement";
import { formatMoney, offersCopy } from "../offers/offersCopy";
import { useOfferSlots, type OfferSlotBanner } from "./offerSlot";

/**
 * Operator-chosen size → a scale factor applied to a slot's intrinsic
 * dimensions (fonts, padding, QR pixels). The template still owns *where* the
 * slot sits; this only changes how large it draws within that anchor.
 */
const SIZE_SCALE: Record<SlotSize, number> = {
  // Deliberately the SAME ratios as SIZE_WIDTH below. They used to disagree -
  // "small" shrank the box to 62% while its type stayed at 80% - so a small
  // card was full-size words in a box too short for them, and `overflow:
  // hidden` ate the price. Type has to shrink with the box it lives in.
  small: 0.62,
  medium: 0.8,
  large: 1,
};

/**
 * How much of the template's slot width a card actually takes.
 *
 * Separate from {@link SIZE_SCALE}, which scales type and padding. "Large" is
 * the template's own anchor width - the size every scheme was laid out around -
 * so the two smaller settings shrink from it rather than "medium" growing past
 * what the template budgeted for.
 */
const SIZE_WIDTH: Record<SlotSize, number> = {
  small: 0.62,
  medium: 0.8,
  large: 1,
};

/** Round a base dimension by the slot's size scale (keeps pixels integral). */
function scaled(base: number, size: SlotSize, density = 1): number {
  return Math.round(base * SIZE_SCALE[size] * density);
}

/**
 * How much the offer card's TYPE shrinks when it shares the row.
 *
 * The cell is a square cut from the template's slot width, so two cards make
 * each square roughly half as tall as one - but the words inside were sized for
 * a card that had the row to itself. On Nord (420 px slot) a two-up row left an
 * ~57 px text band holding ~130 px of kicker, title, claim and price, and
 * `overflow: hidden` ate the bottom of the card (the "Boka" line, the price).
 * Type has to shrink with the box it lives in, exactly as SIZE_SCALE says.
 */
const ROW_DENSITY: Record<number, number> = { 1: 1, 2: 0.78, 3: 0.66 };

/**
 * Shared, token-driven rendering for template feature-slots (Announcement / QR).
 *
 * The TEMPLATE owns the slot's position and look: it passes a {@link SlotStyle}
 * built from its own palette/fonts and an absolute-positioned `containerStyle`.
 * This library owns the slot's *structure* and behaviour (active-window gating,
 * QR encoding, layout) so a slot is built once and every template inherits it.
 *
 * The operator never reaches these - they only supply data (see ContentEditor's
 * SlotsEditor), and the template decides where it lands and what colour it is.
 */
export interface SlotStyle {
  /** Panel / card background. */
  bg: string;
  /** Primary text colour. */
  ink: string;
  /** Secondary (eyebrow / caption) text colour. */
  inkDim: string;
  /** Accent - eyebrow text, QR foreground, focus hairline. */
  accent: string;
  /** Hairline / border colour. Defaults to a faint ink. */
  rule?: string;
  /** Body font stack. */
  font?: string;
  /** Eyebrow / label font stack. Defaults to `font`. */
  kickerFont?: string;
  /** Corner radius (px). Default 4. */
  radius?: number;
  /** QR light backing colour. Default "#ffffff". */
  qrBg?: string;
}

interface SchemeSlotProps {
  /** Manifest slot id to render the operator's fill for. */
  slotId: string;
  /** Template-owned styling derived from its tokens. */
  style: SlotStyle;
  /** Absolute-position wrapper supplied by the template (top/left/width…). */
  containerStyle?: CSSProperties;
  /**
   * Offer fills only: the template's focus ring is currently on this slot.
   *
   * Announcement and QR fills ignore it - they are inert decoration and have
   * always been. An offer fill is the one fill a guest can PRESS, so the
   * template that owns the placement also owns where the banner sits in its own
   * d-pad order (see `useOfferSlot`, which tells it whether there is a cell to
   * add at all).
   */
  focused?: boolean;
  /**
   * Offer fills only: what OK does. Given the offer's id, so the template can
   * open that offer rather than the shop's front door.
   *
   * Omitted means the banner draws as information: no focus ring, no "OK"
   * affordance, nothing that promises a press the template cannot deliver.
   */
  onPress?: (offerId: string) => void;
}

/**
 * Host for one template slot. Reads the operator's fill, hides itself unless the
 * slot is enabled, non-empty and inside its active window, and renders the
 * right primitive. Returns null when there's nothing to show - templates can
 * drop it anywhere without guarding.
 */
export function SchemeSlot({
  slotId,
  style,
  containerStyle,
  focused,
  onPress,
}: SchemeSlotProps) {
  const scheme = useScheme();
  const { content, knobs, context } = scheme;
  const now = useNow();
  // Resolved unconditionally (hooks cannot sit behind the early returns below)
  // and against the same clock the gating uses. `null` for every fill that is
  // not a live offer - including the dangling and expired ones, which is where
  // the fail-soft rule is actually kept (`offerSlot.ts`).
  // Resolved unconditionally (hooks cannot sit behind the early returns below)
  // and against the same clock the gating uses. One entry per fill, `null` for
  // every fill that is not a LIVE offer - including the dangling and expired
  // ones, which is where the fail-soft rule is actually kept (`offerSlot.ts`).
  const banners = useOfferSlots(slotId, now);
  // The promo slot is gated by the template's `showPromotions` toggle - the
  // single on/off on the CMS Screens board. When the template declares the knob
  // and it's off, the slot never renders (the CMS marks its editor tab "Off").
  if (knobs.has("showPromotions") && !knobs.toggle("showPromotions")) {
    return null;
  }
  const fills = content.slots[slotId] ?? [];
  const locale = context.locale ?? "en";
  const fallback = context.defaultLocale ?? locale;

  // Each fill is asked separately whether it draws, so one expired card does not
  // take its neighbours with it - and a slot whose every card is spent renders
  // nothing at all rather than an empty row holding space open.
  const cards = fills
    .map((fill, i) => ({ fill, banner: banners[i] ?? null }))
    .filter(({ fill, banner }) => {
      if (!slotActive(fill, now)) return false;
      // An offer that was deleted, disabled, or is outside its window renders
      // NOTHING - not a placeholder and not a gap the layout shifts around.
      if (fill.type === "offer") return !!banner;
      return true;
    });
  if (cards.length === 0) return null;

  const labelFor = (
    fill: (typeof fills)[number],
    banner: OfferSlotBanner | null
  ) => {
    if (fill.type === "offer") {
      const title = banner
        ? resolveLocalized(banner.offer.title, locale, fallback)
        : "";
      // The line as the operator TYPED it, never the interpolated one:
      // `SlotImpression` keys its dwell on the label, so a live count would end
      // one impression and start another every time somebody else booked.
      return (fill.headline || title || slotId).slice(0, 120);
    }
    const own =
      fill.type === "announcement" ? (fill.title ?? fill.body) : fill.label;
    return (own ?? slotId).slice(0, 120);
  };

  // ONE size for the row, taken from the first card. Size is a property of the
  // block an operator placed on the home screen, not of each card inside it -
  // a row whose cards were different sizes read as a mistake rather than a
  // layout, and there is no question a per-card size answers.
  const rowSize: SlotSize = cards[0]?.fill.size ?? "medium";
  const gap = cards.length > 1 ? 16 : 0;
  return (
    // Two boxes, deliberately. The OUTER one is the template's own anchor and
    // width, untouched - overriding its width with a percentage would resolve
    // against the hero behind it instead of the space the template budgeted.
    // The INNER one is the row, scaled by the operator's size within that.
    <div style={containerStyle}>
      <div
        style={{
          width: `${Math.round(SIZE_WIDTH[rowSize] * 100)}%`,
          display: "flex",
          // Side by side, sharing the slot's width. The template still owns
          // where the row sits; how many cards are in it is the operator's call.
          flexDirection: "row",
          alignItems: "stretch",
          gap,
        }}
      >
        {cards.map(({ fill, banner }, i) => (
          <SlotImpression
            key={`${slotId}-${i}`}
            slotId={i === 0 ? slotId : `${slotId}-${i + 1}`}
            label={labelFor(fill, banner)}
          >
            {/* Equal cell per card: same width from `flex: 1 1 0`, same height
              from the square. An offer beside a QR used to be two different
              shapes in one row. */}
            <div
              style={{
                flex: "1 1 0",
                minWidth: 0,
                aspectRatio: "1 / 1",
                display: "flex",
              }}
            >
              {fill.type === "offer" && banner ? (
                <OfferBanner
                  banner={banner}
                  size={fill.size}
                  density={ROW_DENSITY[cards.length] ?? 0.66}
                  style={style}
                  focused={!!focused && i === 0}
                  onPress={onPress && (() => onPress(banner.offer.id))}
                  locale={locale}
                  defaultLocale={fallback}
                />
              ) : fill.type === "announcement" ? (
                <AnnouncementBanner slot={fill} style={style} />
              ) : fill.type === "qr" ? (
                <QrBlock slot={fill} style={style} />
              ) : null}
            </div>
          </SlotImpression>
        ))}
      </div>
    </div>
  );
}

/**
 * Counts one promotion impression, timed.
 *
 * This is the measurement the content-ROI report is built on, and it is
 * deliberately mount-to-unmount rather than "the page containing it was
 * opened": a slot that is gated out of its active window, hidden behind a
 * toggle, or on a screen the guest never reached has no impression, so a
 * property comparing two promotions is comparing time actually on glass.
 *
 * It lives in its own component because {@link SchemeSlot} returns null through
 * several early exits before it knows whether there is anything to count, and a
 * hook cannot sit behind a conditional return. Mounting this only on the
 * visible path also means the effect's lifetime IS the impression's lifetime,
 * with no visibility bookkeeping of its own.
 */
function SlotImpression({
  slotId,
  label,
  children,
}: {
  slotId: string;
  label: string;
  children: ReactNode;
}) {
  useEffect(() => startDwell("slot_view", slotId, label), [slotId, label]);
  return <>{children}</>;
}

function AnnouncementBanner({
  slot,
  style,
}: {
  slot: ResolvedAnnouncementSlot;
  style: SlotStyle;
}) {
  const rule = style.rule ?? "rgba(0,0,0,0.12)";
  const font = style.font ?? "inherit";
  const kickerFont = style.kickerFont ?? font;
  const size = slot.size;
  return (
    <div
      style={{
        display: "flex",
        alignItems: "stretch",
        gap: 0,
        background: style.bg,
        color: style.ink,
        borderRadius: style.radius ?? 4,
        boxShadow: `inset 0 0 0 1px ${rule}`,
        overflow: "hidden",
        fontFamily: font,
      }}
    >
      {slot.imageUrl && (
        <div
          style={{
            flex: "0 0 auto",
            width: scaled(132, size),
            alignSelf: "stretch",
            backgroundImage: `url(${slot.imageUrl})`,
            backgroundSize: "cover",
            backgroundPosition: "center",
          }}
          aria-hidden
        />
      )}
      <div
        style={{
          flex: "1 1 auto",
          minWidth: 0,
          minHeight: 0,
          padding: `${scaled(18, size)}px ${scaled(22, size)}px`,
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          gap: 6,
          // Along the top edge now that the photo is above it rather than
          // beside it - the rule still says "this block belongs to that image".
          borderTop: `3px solid ${style.accent}`,
        }}
      >
        {slot.title && (
          <div
            style={{
              fontFamily: kickerFont,
              fontSize: scaled(12, size),
              letterSpacing: "0.14em",
              textTransform: "uppercase",
              color: style.accent,
            }}
          >
            {slot.title}
          </div>
        )}
        <div
          style={{
            fontSize: scaled(20, size),
            lineHeight: 1.32,
            color: style.ink,
            // Keep banners to a tidy two lines on any template.
            display: "-webkit-box",
            WebkitLineClamp: 2,
            WebkitBoxOrient: "vertical",
            overflow: "hidden",
          }}
        >
          {slot.body}
        </div>
      </div>
    </div>
  );
}

/**
 * One offer, featured on the home screen (`POS-4`).
 *
 * Everything except the operator's scarcity line is read from the catalogue at
 * render time, so this banner cannot disagree with the Offers screen behind it:
 * same title, same price, same seat ledger, one source. It borrows the Offers
 * screen's own copy pack for its chrome ("Boka", "2 platser kvar", "Fullbokat")
 * for the same reason - a guest must not be told two different words for one
 * thing on two screens of the same television.
 *
 * A FULLY BOOKED offer still draws, with the seats line replaced by "Fullbokat".
 * Hiding it would make an operator's promotion vanish for reasons they cannot
 * see from the CMS, and the honest version is more useful to the guest than
 * silence: the spa evening exists, tonight is gone.
 */
function OfferBanner({
  banner,
  size,
  density = 1,
  style,
  focused,
  onPress,
  locale,
  defaultLocale,
}: {
  banner: OfferSlotBanner;
  size: SlotSize;
  /** Row-sharing shrink, see {@link ROW_DENSITY}. 1 = the card is alone. */
  density?: number;
  style: SlotStyle;
  focused: boolean;
  onPress?: () => void;
  locale: string;
  defaultLocale: string;
}) {
  const { offer, remaining, fullyBooked, headline } = banner;
  const copy = offersCopy(locale);
  const L = (v: Parameters<typeof resolveLocalized>[0]) =>
    resolveLocalized(v, locale, defaultLocale);
  const rule = style.rule ?? "rgba(0,0,0,0.12)";
  const font = style.font ?? "inherit";
  const kickerFont = style.kickerFont ?? font;
  const radius = style.radius ?? 4;
  const price =
    offer.action.kind === "promotion" ? undefined : offer.action.price;
  // What the second line says, in order of how much it is worth reading: the
  // operator's own claim, then the true seat count, then the offer's own words.
  const claim = fullyBooked
    ? copy.soldOut
    : headline || (remaining != null ? copy.seatsLeft(remaining) : "");
  const pressable = !!onPress;
  const sc = (base: number) => scaled(base, size, density);
  // Sharing the row: one line each for title and claim, and a shorter photo
  // band, so the price and the "OK · open" hint always fit inside the square.
  const dense = density < 1;
  const lines = dense ? 1 : 2;
  return (
    <button
      type="button"
      data-testid={`slot-offer-${offer.id}`}
      aria-current={focused ? "true" : undefined}
      disabled={!pressable}
      onClick={onPress}
      style={{
        appearance: "none",
        // A SQUARE, not a strip. This card sits on top of the property photo on
        // the home screen and has to read as an offer from three metres, so it
        // is given real estate rather than a headline-sized sliver. The template
        // owns the anchor and the full width; the square is derived from it, so
        // every scheme gets the same shape without eight separate height
        // decisions.
        //
        // Fills the cell the row gave it. Size is applied once, to the row.
        width: "100%",
        height: "100%",
        textAlign: "left",
        display: "flex",
        flexDirection: "column",
        alignItems: "stretch",
        padding: 0,
        background: style.bg,
        color: style.ink,
        borderRadius: radius,
        border: `1px solid ${focused ? style.accent : rule}`,
        // A ring rather than a size change: a banner that grew on focus would
        // shove the tiles under it on every arrow press.
        boxShadow: focused ? `0 0 0 5px ${style.accent}55` : "none",
        transition: "box-shadow 160ms ease, border-color 160ms ease",
        overflow: "hidden",
        fontFamily: font,
        cursor: pressable ? "pointer" : "default",
      }}
    >
      {offer.imageUrl && (
        <div
          style={{
            // The photo is the top band of the square, not a strip down its
            // side: a 132px sliver of a spa photo is a texture, and a guest
            // reads a picture of the thing before they read its name.
            flex: dense ? "0 0 46%" : "0 0 56%",
            width: "100%",
            backgroundImage: `url(${offer.imageUrl})`,
            backgroundSize: "cover",
            backgroundPosition: "center",
          }}
          aria-hidden
        />
      )}
      <div
        style={{
          flex: "1 1 auto",
          minWidth: 0,
          padding: `${sc(16)}px ${sc(22)}px`,
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          gap: 4,
          borderLeft: `3px solid ${style.accent}`,
        }}
      >
        <div
          style={{
            fontFamily: kickerFont,
            fontSize: sc(12),
            letterSpacing: "0.14em",
            textTransform: "uppercase",
            color: style.accent,
            lineHeight: 1.3,
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
          }}
        >
          {copy.kind[offer.action.kind]}
          {offer.category ? ` · ${offer.category}` : ""}
        </div>
        <div
          style={{
            fontSize: sc(20),
            fontWeight: 600,
            // 1.25, never tighter: this box clips, and a TV slices the
            // descenders off g/y/p/j at 1.0. See
            // docs/memory/global/pitfalls/tv-tight-line-height-clipping.md.
            lineHeight: 1.25,
            color: style.ink,
            display: "-webkit-box",
            WebkitLineClamp: lines,
            WebkitBoxOrient: "vertical",
            overflow: "hidden",
          }}
        >
          {L(offer.title)}
        </div>
        <div
          style={{
            fontSize: sc(16),
            lineHeight: 1.35,
            color: claim ? style.accent : style.inkDim,
            fontWeight: claim ? 700 : 400,
            display: "-webkit-box",
            WebkitLineClamp: lines,
            WebkitBoxOrient: "vertical",
            overflow: "hidden",
          }}
        >
          {claim || L(offer.body)}
        </div>
        {(price || pressable) && (
          <div
            style={{
              marginTop: 6,
              display: "flex",
              alignItems: "center",
              gap: sc(12),
              fontSize: sc(14),
              lineHeight: 1.3,
            }}
          >
            {price && (
              <span style={{ fontWeight: 700, color: style.ink }}>
                {formatMoney(price, copy)}
              </span>
            )}
            {pressable && (
              <span
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 8,
                  color: style.inkDim,
                }}
              >
                <span
                  style={{
                    padding: "2px 8px",
                    borderRadius: 999,
                    border: `1px solid ${rule}`,
                    fontFamily: kickerFont,
                    fontSize: sc(11),
                    letterSpacing: "0.1em",
                    lineHeight: 1.4,
                  }}
                >
                  OK
                </span>
                {copy.hint.open}
              </span>
            )}
          </div>
        )}
      </div>
    </button>
  );
}

function QrBlock({ slot, style }: { slot: ResolvedQrSlot; style: SlotStyle }) {
  const rule = style.rule ?? "rgba(0,0,0,0.12)";
  const font = style.font ?? "inherit";
  const kickerFont = style.kickerFont ?? font;
  const qrBg = style.qrBg ?? "#ffffff";
  const size = slot.size;
  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: scaled(16, size),
        background: style.bg,
        color: style.ink,
        borderRadius: style.radius ?? 4,
        boxShadow: `inset 0 0 0 1px ${rule}`,
        padding: scaled(14, size),
        fontFamily: font,
      }}
    >
      <div
        style={{
          flex: "0 0 auto",
          background: qrBg,
          padding: scaled(8, size),
          borderRadius: 4,
          lineHeight: 0,
        }}
      >
        <QRCode
          value={slot.url}
          size={scaled(96, size)}
          bgColor={qrBg}
          fgColor={style.accent}
          level="M"
        />
      </div>
      {slot.label && (
        <div
          style={{
            minWidth: 0,
            display: "flex",
            flexDirection: "column",
            gap: 4,
          }}
        >
          <div
            style={{
              fontFamily: kickerFont,
              fontSize: scaled(11, size),
              letterSpacing: "0.14em",
              textTransform: "uppercase",
              color: style.accent,
            }}
          >
            Scan
          </div>
          <div
            style={{
              fontSize: scaled(17, size),
              lineHeight: 1.3,
              color: style.ink,
            }}
          >
            {slot.label}
          </div>
        </div>
      )}
    </div>
  );
}
